import { Fragment } from 'react';
import { flattenPages, useChangelog } from '@/api/queries';
import {
  InfiniteSentinel,
  ListMessage,
  MonthDividerRow,
  SkeletonRows,
} from '@/components/shared/lists';
import { TabHeader } from '@/app/TabHeader';
import { useAppData } from '@/app/appData';
import { useNavigation } from '@/app/navigation';
import { useIsDesktop } from '@/app/useMediaQuery';
import { de } from '@/i18n/de';
import { ChangeEntry } from './ChangeEntry';

/**
 * The change log, newest first and unbounded in depth.
 *
 * Every mutation invalidates this query, and an invalidated infinite query
 * refetches all of its loaded pages — fetching only while the tab is on screen
 * keeps a deep scroll from doing that after each save elsewhere in the app.
 */
export function ChangesTab() {
  const { persons, now } = useAppData();
  const { tab } = useNavigation();
  const isDesktop = useIsDesktop();
  const query = useChangelog(tab === 'changes');

  const entries = flattenPages(query.data?.pages, 'entries');

  return (
    <div className="nav:max-w-[720px]">
      <TabHeader title={de.tabs.changes} showSettings={!isDesktop} />

      <div className="pt-1.5">
        {query.isError ? <ListMessage>{de.errors.loadFailed}</ListMessage> : null}
        {query.isSuccess && entries.length === 0 ? (
          <ListMessage>{de.changes.empty}</ListMessage>
        ) : null}

        {entries.map((entry, index) => {
          const previous = entries[index - 1];
          const month = monthOfTimestamp(entry.ts);
          const previousMonth = previous ? monthOfTimestamp(previous.ts) : null;
          const dividerNeeded =
            previousMonth !== null &&
            (previousMonth.year !== month.year || previousMonth.month !== month.month);

          return (
            <Fragment key={entry.id}>
              {dividerNeeded ? <MonthDividerRow year={month.year} month={month.month} /> : null}
              <ChangeEntry entry={entry} persons={persons} now={now} />
            </Fragment>
          );
        })}

        {query.isPending || query.isFetchingNextPage ? <SkeletonRows /> : null}

        <InfiniteSentinel
          onReach={() => void query.fetchNextPage()}
          enabled={query.hasNextPage && !query.isFetchingNextPage}
          footer={de.changes.loadingMore}
        />
      </div>
    </div>
  );
}

/** Log timestamps are wall-clock milliseconds, not slot indices. */
function monthOfTimestamp(ts: number): { year: number; month: number } {
  const date = new Date(ts);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}
